"use client";

import {
  Elements,
  PaymentElement,
  useElements,
  useStripe,
} from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import { CreditCard, ShieldCheck } from "lucide-react";
import { useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { createClientId } from "@/lib/client-id";

export type ReservationPaymentSession = {
  publicId: string;
  sessionToken: string;
  objectType: "payment_intent" | "setup_intent";
  bookingMethod: "deposit" | "prepaid" | "card_hold";
  amount: number;
  currency: string;
  clientSecret: string;
  publishableKey: string;
};

export type SavedPaymentMethod = {
  publicId: string;
  brand: string;
  last4: string;
  expMonth?: number;
  expYear?: number;
  isDefault: boolean;
};

export function StripePaymentStep({
  session,
  onBack,
  onComplete,
  context = "public",
  savedPaymentMethods = [],
  verifyEndpoint,
}: {
  session: ReservationPaymentSession;
  onBack: () => void;
  onComplete: () => Promise<void>;
  context?: "public" | "staff";
  savedPaymentMethods?: SavedPaymentMethod[];
  verifyEndpoint?: string;
}) {
  const stripe = useMemo(
    () => loadStripe(session.publishableKey),
    [session.publishableKey],
  );
  return (
    <Elements
      stripe={stripe}
      options={{
        clientSecret: session.clientSecret,
        appearance: {
          theme: "stripe",
          variables: {
            colorPrimary: "#721121",
            borderRadius: "6px",
            fontFamily: "Arial, sans-serif",
          },
        },
      }}
    >
      <StripePaymentForm
        session={session}
        onBack={onBack}
        onComplete={onComplete}
        context={context}
        savedPaymentMethods={savedPaymentMethods}
        verifyEndpoint={verifyEndpoint}
      />
    </Elements>
  );
}

function StripePaymentForm({
  session,
  onBack,
  onComplete,
  context,
  savedPaymentMethods,
  verifyEndpoint,
}: {
  session: ReservationPaymentSession;
  onBack: () => void;
  onComplete: () => Promise<void>;
  context: "public" | "staff";
  savedPaymentMethods: SavedPaymentMethod[];
  verifyEndpoint?: string;
}) {
  const stripe = useStripe();
  const elements = useElements();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const isCardHold = session.objectType === "setup_intent";

  const submitSavedMethod = async (paymentMethod: SavedPaymentMethod) => {
    if (!stripe) return;
    setSubmitting(true);
    setError("");
    try {
      const response = await fetch(
        `/api/tables/account/payment-sessions/${session.publicId}/confirm-saved-method`,
        {
          method: "POST",
          headers: {
            "content-type": "application/json",
            "idempotency-key": createClientId(),
          },
          body: JSON.stringify({
            sessionToken: session.sessionToken,
            paymentMethodPublicId: paymentMethod.publicId,
          }),
        },
      );
      const payload = await response.json();
      if (!response.ok)
        throw new Error(payload.detail ?? payload.message ?? "The saved card could not be used.");
      if (payload.status === "requires_action" && payload.clientSecret) {
        const action = await stripe.handleNextAction({ clientSecret: payload.clientSecret });
        if (action.error)
          throw new Error(action.error.message ?? "The saved card could not be authenticated.");
        const verified = await fetch(
          verifyEndpoint ?? `/api/tables/payment-sessions/${session.publicId}/verify`,
          {
            method: "POST",
            headers: { "content-type": "application/json" },
            body: JSON.stringify({ sessionToken: session.sessionToken }),
          },
        );
        const verification = await verified.json();
        if (!verified.ok || verification.status !== "succeeded")
          throw new Error("Stripe is still processing the saved card.");
      } else if (payload.status !== "succeeded") {
        throw new Error(`The saved card is currently ${payload.status}.`);
      }
      await onComplete();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "The saved card could not be used.");
      setSubmitting(false);
    }
  };

  const submit = async () => {
    if (!stripe || !elements) return;
    setSubmitting(true);
    setError("");
    const result = isCardHold
      ? await stripe.confirmSetup({ elements, redirect: "if_required" })
      : await stripe.confirmPayment({ elements, redirect: "if_required" });
    if (result.error) {
      setError(
        result.error.message ?? "Stripe could not complete the payment step.",
      );
      setSubmitting(false);
      return;
    }
    const verified = await fetch(
      verifyEndpoint ?? "/api/tables/payment-sessions/" + session.publicId + "/verify",
      {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ sessionToken: session.sessionToken }),
      },
    );
    const payload = await verified.json();
    if (!verified.ok || payload.status !== "succeeded") {
      setError(
        payload.message ??
          "Stripe is still processing this payment. Try again in a moment.",
      );
      setSubmitting(false);
      return;
    }
    try {
      await onComplete();
    } catch (cause) {
      setError(
        cause instanceof Error
          ? cause.message
          : "The reservation could not be completed.",
      );
      setSubmitting(false);
    }
  };

  return (
    <div
      className={
        "booking-body booking-details stripe-payment-step reservation-payment-step " +
        context
      }
    >
      <p className="step-label">Secure payment</p>
      <h2>
        {isCardHold
          ? "Save a card for this reservation."
          : "Complete the payment."}
      </h2>
      <div className="stripe-payment-summary">
        <CreditCard />
        <span>
          <strong>
            {isCardHold
              ? "Card hold authorization"
              : session.bookingMethod === "deposit"
                ? "Reservation deposit"
                : "Prepaid reservation"}
          </strong>
          <small>
            {formatMoney(session.amount, session.currency)} · processed by
            Stripe
          </small>
        </span>
      </div>
      {savedPaymentMethods.length > 0 && (
        <div className="saved-payment-options">
          <strong>Use a saved card</strong>
          {savedPaymentMethods.map((method) => (
            <button
              type="button"
              key={method.publicId}
              disabled={submitting}
              onClick={() => void submitSavedMethod(method)}
            >
              <CreditCard />
              <span><b>{method.brand} •••• {method.last4}</b><small>{method.expMonth && method.expYear ? `Expires ${String(method.expMonth).padStart(2, "0")}/${String(method.expYear).slice(-2)}` : "Saved with Stripe"}</small></span>
              <em>{method.isDefault ? "Default" : "Use"}</em>
            </button>
          ))}
          <span>Or enter another card</span>
        </div>
      )}
      <PaymentElement options={{ layout: "tabs" }} />
      {isCardHold && (
        <p className="stripe-payment-disclosure">
          <ShieldCheck /> The card is saved with Stripe and is not charged now.
          The venue may charge up to{" "}
          {formatMoney(session.amount, session.currency)} under the reservation
          policy.
        </p>
      )}
      {error && (
        <p className="booking-error" role="alert">
          {error}
        </p>
      )}
      <Button
        size="lg"
        className="button button--burgundy button--full"
        disabled={!stripe || submitting}
        onClick={() => void submit()}
      >
        {submitting
          ? "Confirming with Stripe…"
          : isCardHold
            ? "Save card and confirm"
            : "Pay " +
              formatMoney(session.amount, session.currency) +
              " and confirm"}
      </Button>
      <button
        className="confirmation-done"
        type="button"
        disabled={submitting}
        onClick={onBack}
      >
        Back to payment options
      </button>
    </div>
  );
}

const formatMoney = (amount: number, currency: string) =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency,
  }).format(amount / 100);
